import { Section, SectionHeading } from "./Section";
import { Reveal } from "./Reveal";
import { IsoGrid, IsoIllustration, IsoStep } from "./Iso";
import { addonHowItWorks } from "@/content/addon-howitworks";
import { ISO, type IsoKey } from "@/content/illustrations";
import { GhostCta, PrimaryCta } from "./Cta";
import type { PricingAddonStep } from "@/lib/pricing.types";
import { useT } from "@/lib/i18n-dict";

/**
 * "How it works" band on an add-on page — each step is headed by an isometric
 * illustration from the brand pack so the add-on reads as a visual journey.
 */
export function AddonHowItWorks({
  slug,
  name,
  steps: cmsSteps = [],
  lead: cmsLead = "",
}: {
  slug: string;
  name: string;
  /** Steps managed in the CMS; the bundled content is used when empty. */
  steps?: PricingAddonStep[];
  lead?: string;
}) {
  const fallback = addonHowItWorks(slug);
  const t = useT();
  const lead = cmsLead.trim() || fallback.lead;
  const steps = cmsSteps.length
    ? cmsSteps.map((step) => ({
        title: step.title,
        body: step.body,
        iso: (step.iso in ISO ? step.iso : "storytelling") as IsoKey,
        image: step.image_url.trim(),
      }))
    : fallback.steps.map((step) => ({ ...step, image: "" }));

  if (!steps.length) return null;

  return (
    <Section className="relative bg-surface/40">
      <IsoGrid intensity="soft" />
      <SectionHeading
        eyebrow={t("How it works")}
        title={`${name}, ${t("step by step")}`}
        lead={t(lead)}
      />
      <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {steps.map((step, index) => (
          <Reveal key={step.title} variant="iso" delay={index * 60}>
            <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-colors hover:border-primary/50">
              <div className="flex h-52 items-center justify-center border-b border-border bg-surface/60 p-6">
                {step.image ? (
                  <img
                    src={step.image}
                    alt=""
                    loading="lazy"
                    className="max-h-full w-auto max-w-[14rem] object-contain transition-transform duration-500 group-hover:scale-[1.03] motion-reduce:transform-none"
                  />
                ) : (
                  <IsoIllustration
                    name={step.iso}
                    alt=""
                    className="max-h-full w-auto max-w-[14rem] object-contain transition-transform duration-500 group-hover:scale-[1.03] motion-reduce:transform-none"
                  />
                )}
              </div>
              <div className="flex flex-1 flex-col p-6">
                <div className="flex items-start gap-4">
                  <IsoStep n={index + 1} />
                  <h3 className="font-display text-xl font-bold text-balance-tight">
                    {step.title}
                  </h3>
                </div>
                <p className="mt-4 text-base text-muted-foreground md:text-lg">{step.body}</p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      {/* Contextual CTA — carries the add-on into the demo flow */}
      <Reveal variant="iso" delay={steps.length * 60}>
        <div className="iso-plate mt-12 flex flex-col gap-6 rounded-2xl border border-primary/30 bg-surface p-8 shadow-[var(--glow-primary)] md:flex-row md:items-center md:justify-between md:p-10">
          <div className="flex items-center gap-6">
            <img
              src={ISO.teamwork}
              alt=""
              aria-hidden
              loading="lazy"
              className="hidden w-32 shrink-0 select-none [transform:rotateX(6deg)_rotateZ(-2deg)] sm:block"
            />
            <div>
              <h3 className="font-display text-2xl font-bold text-balance-tight">
                {t("See {name} running on your own process").replace("{name}", name)}
              </h3>
              <p className="mt-3 max-w-xl text-base text-muted-foreground md:text-lg">
                {t(
                  "Book a 30-minute session — we walk these steps through with your data and give you a rollout estimate for",
                )}{" "}
                {name}.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-3">
            <PrimaryCta to="/registration-demo" search={{ addon: slug }}>
              {t("Book a")} {name} {t("demo")}
            </PrimaryCta>
            <GhostCta to="/contacts">Talk to a consultant</GhostCta>
          </div>
        </div>
      </Reveal>
    </Section>
  );
}
